import { differenceInMilliseconds } from 'date-fns';
import { CombinedRouteLegType } from '@lp/features/RoutePlanner/types';
import {
  AircraftLegResource,
  AviaLegTypes,
  BaseLegResourceType,
  GroundLegTypes,
  LegLocationTypeEnum,
  RouteLocationType,
  RouteLegTypeEnum,
  SegmentTypeEnum,
} from '@/api/apis/route';
import { CaseResourceType, OrganType } from '@/api/apis/case';
import { AircraftAvailabilityView } from '@/api/apis/resources';
import { RouteSegmentType } from '@/types/route';
import { getSegmentMapKey, isAviaLegType, isGroundLegType, REPOSITION_SEGMENT_TYPES } from '@/utils/route';
import { convertMsToTimeString, WithExtendedLocation } from '@/utils/helpers';
import { combineSimilarTypeLegs } from './route';
import { getBaseParentNode, getBaseSegmentNode } from './nodes';

export type TreeNodeInfo = {
  id: string;
  title: string;
  locationType: LegLocationTypeEnum;
  organType: OrganType;
  segmentType: SegmentTypeEnum;
};

export type TreeTransport = {
  legType: RouteLegTypeEnum;
  time: string;
  aircraft?: AircraftAvailabilityView;
  aircraftIndex: number;
  isReposition: boolean;
} | null;

export type TreeLeg = {
  id: string;
  start: TreeNodeInfo;
  end: TreeNodeInfo;
  transport: TreeTransport;
  branchIndex: number;
};

const buildTransport = (
  leg: CombinedRouteLegType<AviaLegTypes | GroundLegTypes> | null,
  aircrafts: BaseLegResourceType<AircraftLegResource>[],
): TreeTransport => {
  if (!leg) return null;

  const aircraftRes = leg.resources?.find((r) => r.resourceType === CaseResourceType.Aircraft);
  const aircraft = (aircraftRes && aircrafts.find((a) => a.id === aircraftRes.resourceId)) || undefined;
  const isMultipleAircrafts = aircrafts.length > 1;
  const aircraftIndex = isMultipleAircrafts ? aircrafts.findIndex((a) => a.id === aircraft?.id) + 1 : 0;

  return {
    legType: leg.legType,
    time: convertMsToTimeString(leg.segmentDurationInMs || 0, 'short'),
    aircraft: aircraft?.data,
    aircraftIndex: Math.max(aircraftIndex, 0),
    isReposition: REPOSITION_SEGMENT_TYPES.includes(leg.segmentType),
  };
};

type ComputeUnconnectedLegsParams = {
  segments: RouteSegmentType[];
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  aircrafts: BaseLegResourceType<AircraftLegResource>[];
  locations: WithExtendedLocation<RouteLocationType>[];
  organType: OrganType;
  schemaType: 'main' | 'alt';
};

export const computeUnconnectedLegs = ({
  segments,
  legs,
  aircrafts,
  locations,
  organType,
  schemaType,
}: ComputeUnconnectedLegsParams): TreeLeg[] => {
  return segments.flatMap((s): TreeLeg[] => {
    const segmentLegs = legs.get(getSegmentMapKey(s)) || [];
    const airLegs = segmentLegs.filter(isAviaLegType);
    const currentLegs = airLegs.length ? airLegs : segmentLegs;
    const currentLeg = combineSimilarTypeLegs(currentLegs);

    if (!currentLeg) return [];

    const startDataBaseNode = getBaseSegmentNode(s, s.startLocation, segmentLegs, locations);
    const startData = getBaseParentNode(startDataBaseNode, organType, schemaType);
    const endDataBaseNode = getBaseSegmentNode(s, s.endLocation, segmentLegs, locations);
    const endData = getBaseParentNode(endDataBaseNode, organType, schemaType);

    return segmentLegs.flatMap((leg, legIndex): TreeLeg[] => {
      const isGround = isGroundLegType(leg);
      const depRef = isGround
        ? leg.departureLocation.locationReference || leg.departureLocation.airportReference
        : leg.departureLocation;
      const arrRef = isGround
        ? leg.arrivalLocation.locationReference || leg.arrivalLocation.airportReference
        : leg.arrivalLocation;

      if (!depRef || !arrRef) return [];

      const segmentDurationInMs = differenceInMilliseconds(new Date(leg.arrivalTime), new Date(leg.departureTime));
      const combinedRouteLeg = { ...leg, segmentDurationInMs };

      return [
        {
          id: `${s.id}-leg-${legIndex}`,
          start: {
            id: `${s.id}-leg-${legIndex}-start`,
            title: startData.data.title,
            locationType: depRef.locationType as LegLocationTypeEnum,
            organType,
            segmentType: s.type as SegmentTypeEnum,
          },
          end: {
            id: `${s.id}-leg-${legIndex}-end`,
            title: endData.data.title,
            locationType: arrRef.locationType as LegLocationTypeEnum,
            organType,
            segmentType: s.type as SegmentTypeEnum,
          },
          transport: buildTransport(combinedRouteLeg, aircrafts),
          branchIndex: 0,
        },
      ];
    });
  });
};
